import {
  type ComponentType,
  createContext,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useHotkeys } from "react-hotkeys-hook";
import { type QueryClient, useQueryClient } from "react-query";

import { useUser } from "src/contexts/user-context";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import {
  type AmpBoardProjectQuery,
  type AmpBoardProjectsQuery,
  type AmpBoardTagsQuery,
  type AmpBoardTaskQuery,
  type AmpBoardTasksQuery,
  type AmpBoardUserFragment,
  type LatestActivityQuery,
  type AmpProjectLinkFragment,
  type AmpProjectLinkTargetInput,
  type AmpProjectUserFragment,
  type AmpProjectRowFragment,
  type AmpProjectsSetInput,
  type AmpTaskUserFragment,
  type AmpTasksSetInput,
  type AmpTaskRowFragment,
  type AmpWorkflowStatusFragment,
  type CreateAmpTaskInput,
  useAddAmpBoardTaskBlockerMutation,
  useAmpBoardProjectQuery,
  useAmpBoardProjectsQuery,
  useAmpBoardTaskQuery,
  useLatestActivityQuery,
  useCreateAmpBoardTaskMutation,
  useLinkAmpBoardWorkMutation,
  useMoveAmpBoardTaskMutation,
  useRemoveAmpBoardTaskBlockerMutation,
  useReorderAmpBoardTasksMutation,
  useSetAmpBoardTaskStatusMutation,
  useTagAmpBoardTaskMutation,
  useUntagAmpBoardTaskMutation,
  useUnlinkAmpBoardWorkMutation,
  useUpdateAmpBoardProjectUsersMutation,
  useUpdateAmpBoardProjectMutation,
  useUpdateAmpBoardTaskUsersMutation,
  useUpdateAmpBoardTaskMutation,
} from "src/graphql";
import { invalidateLinkedProjects } from "src/pages/projects/use-linked-projects";
import {
  type BoardQueryName,
  boardQueryMatcher,
  CREATED_TASKS_KEY,
  dropLoadedCreatedTasks,
  findProject,
  findTask,
  isPendingId,
  newPendingId,
  nextTaskPosition,
  type ProjectPatch,
  patchProjectDetail,
  patchProjectList,
  patchTaskDetail,
  patchTaskList,
  patchTasksOfProject,
  previousValues,
  revertableUpdate,
  swapPendingId,
  type TaskMove,
  unchangedSince,
  type TaskPatch,
  type TaskUpdate,
  updateCached,
} from "src/pages/projects/board-cache";
import { shouldIgnoreCreateTaskShortcut } from "src/pages/projects/board-shortcuts";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import {
  ARCHIVED_PROJECTS_VARIABLES,
  type BoardMember,
  OPEN_PROJECTS_VARIABLES,
  useBoardErrorToast,
  useTags,
  useGroups,
  useWorkflows,
  useWorkspaceMembers,
} from "src/pages/projects/hooks";
import {
  startInputOf,
  startStatusOf,
  type SubtaskCategory,
  type TaskStart,
} from "src/pages/projects/utils";
import { useSyncTaskApprovals } from "src/pages/projects/use-sync-task-approvals";
import { useParams } from "src/router";

export { OVERLAY_SELECTOR } from "src/pages/projects/board-shortcuts";

/** Opens the new-task dialog from anywhere in the area, as in most trackers. */
export const CREATE_TASK_SHORTCUT = "c";

// What each kind of write changes on the server, so its refetch leaves the area's lookups alone.
const TASK_QUERIES: readonly BoardQueryName[] = [
  "AmpBoardTasks",
  "AmpBoardTaskGroups",
  "AmpBoardTask",
  // A project's progress counts its tasks.
  "AmpBoardProjects",
  "AmpBoardProject",
  // A status move starts the task's agent turn before it returns.
  "AmpBoardAgentTurns",
];
// An open picker filters loops from the blocking edges it loaded.
const BLOCKER_QUERIES: readonly BoardQueryName[] = [
  ...TASK_QUERIES,
  "AmpBoardBlockerOptions",
];
const PROJECT_QUERIES: readonly BoardQueryName[] = [
  "AmpBoardProjects",
  "AmpBoardProject",
  // Archiving moves a project's cover between the open and archived lists.
  "AmpBoardProjectCovers",
];
const LINK_QUERIES: readonly BoardQueryName[] = [
  // List rows and cards count a project's work.
  "AmpBoardProjects",
  "AmpBoardProject",
  // Gallery cards and list rows show a project's attachments as tiles.
  "AmpBoardProjectCovers",
  "AmpBoardTask",
  // Task cards and rows show their attachments.
  "AmpBoardTasks",
];

type Tag = Omit<AmpBoardTagsQuery["amp_tags"][number], "task_tags_aggregate">;

type CreatePreset = {
  projectId?: string;
  /** Without it, the new task goes to whoever creates it. */
  ownerUserIds?: string[];
  assigneeUserIds?: string[];
  dueAt?: string;
};

/** A task starts where the board means it to; a subtask starts in the backlog category. */
type NewTaskParent =
  | { kind: "project"; start: TaskStart }
  | { kind: "task"; taskId: string };

type NewTask = Omit<
  CreateAmpTaskInput,
  "assignees" | "reviewers" | "links" | "statusId" | "parentTaskId"
> & {
  projectId: string;
  parent: NewTaskParent;
  assigneeUserIds?: string[];
  assigneeGroupIds?: string[];
};

type StartColumns = Pick<
  AmpTaskRowFragment,
  "parent_task_id" | "status_id" | "status_category" | "status"
>;

/** Columns are undefined while the board lacks the statuses of a new task's workflow. */
function newTaskStartOf({
  parent,
  defaultWorkflowId,
  statusesFor,
}: {
  parent: NewTaskParent;
  defaultWorkflowId: string | null;
  statusesFor: (workflowId: string | null) => AmpWorkflowStatusFragment[];
}): {
  input: Pick<CreateAmpTaskInput, "parentTaskId" | "statusId">;
  columns: StartColumns | undefined;
} {
  switch (parent.kind) {
    case "project": {
      const status = startStatusOf({
        start: parent.start,
        defaultWorkflowId,
        statusesFor,
      });
      return {
        input: startInputOf(parent.start),
        columns: status && {
          parent_task_id: null,
          status_id: status.id,
          status_category: null,
          status,
        },
      };
    }
    case "task": {
      return {
        input: { parentTaskId: parent.taskId },
        columns: {
          parent_task_id: parent.taskId,
          status_id: null,
          status_category: "backlog",
          status: null,
        },
      };
    }
    default: {
      return parent satisfies never;
    }
  }
}

// Subtasks count toward their parent's progress, which lists show on its row.
type SubtaskCounts = Pick<
  AmpTaskRowFragment,
  "subtasks_total" | "subtasks_done"
>;

function withSubtaskCounts(
  task: SubtaskCounts,
  { total = 0, done = 0 }: { total?: number; done?: number },
): SubtaskCounts {
  const count = (value: { aggregate?: { count: number } | null }) =>
    value.aggregate?.count ?? 0;
  return {
    subtasks_total: {
      aggregate: { count: count(task.subtasks_total) + total },
    },
    subtasks_done: { aggregate: { count: count(task.subtasks_done) + done } },
  };
}

/** The people on a task set as a whole list: its owner, always a user, and its assignees. */
export type TaskPersonType = "owner" | "assignee";

/** A task that blocks another, as the blocked task's row lists it. */
export type TaskBlocker = NonNullable<
  AmpTaskRowFragment["blockers"][number]["source_task"]
>;

const TASK_PERSON_FIELD = {
  owner: "owners",
  assignee: "assignees",
} as const satisfies Record<TaskPersonType, keyof AmpTaskRowFragment>;

type TaskActions = {
  /** Resolves to the new task's id, and throws if the server refuses it. */
  create: (task: NewTask) => Promise<string>;
  /** Also brings an archived task back, as the server does. */
  setStatus: (
    task: Pick<AmpTaskRowFragment, "id" | "status_id" | "deleted_at">,
    status: AmpWorkflowStatusFragment,
  ) => void;
  /** Archives a task or brings it back, whatever its status. */
  setArchived: (taskId: string, isArchived: boolean) => void;
  /** A subtask's status, one of the fixed categories rather than a workflow status. */
  setSubtaskStatus: (taskId: string, category: SubtaskCategory) => void;
  /** Makes these users the task's owners or assignees, adding and removing to match; groups stay. */
  setPeople: (taskId: string, type: TaskPersonType, userIds: string[]) => void;
  /** Makes these groups the task's assignee groups, adding and removing to match; users stay. */
  setAssigneeGroups: (taskId: string, userGroupIds: string[]) => void;
  setDueDate: (taskId: string, dueAt: string | null) => void;
  setText: (
    taskId: string,
    fields: { title?: string; description?: string | null },
  ) => void;
  addTag: (taskId: string, name: string) => void;
  removeTag: (taskId: string, tagId: string) => void;
  addBlocker: (taskId: string, blocker: TaskBlocker) => void;
  removeBlocker: (taskId: string, blockerId: string) => void;
  /** Writes new positions, as after a drag within a manually ordered group. */
  reorder: (positions: { id: string; position: number }[]) => void;
  /** Moves a top-level task to a position, shifting the project's tasks between. */
  move: (task: AmpTaskRowFragment, move: TaskMove) => void;
  /** One of a person or a group. */
  addReviewer: (
    taskId: string,
    reviewer: { userId: string | null; userGroupId: string | null },
  ) => void;
  /** Takes the row as the caller sees it, since another cached copy of the task can predate it. */
  removeReviewer: (taskId: string, reviewer: AmpTaskUserFragment) => void;
  /** Archives a subtask and drops it from its parent, which has no archive to show it in. Throws if the server refuses. */
  removeSubtask: (taskId: string) => Promise<void>;
};

type ProjectActions = {
  update: (projectId: string, set: AmpProjectsSetInput) => void;
  /** Attaches a web link to a project, or to one of its tasks. */
  link: (target: {
    projectId: string;
    taskId?: string;
    url: string;
    label: string;
  }) => void;
  /** Attaches existing work to a project, or to one of its tasks. Rejects when the server refuses it. */
  attach: (target: {
    projectId: string;
    taskId?: string;
    target: AmpProjectLinkTargetInput;
  }) => Promise<void>;
  /** Makes these users the project's owners or members; one left out of both leaves it. */
  setPeople: (
    projectId: string,
    people: { ownerIds?: string[]; memberIds?: string[] },
  ) => void;
  /** Removes an attachment from a project or task. */
  unlink: (linkId: string) => void;
};

type BoardWrite<T> = {
  /** Patches the cache, returning how to take the patch back out. */
  apply: () => (() => void) | undefined;
  request: () => Promise<T>;
  errorTitle: string;
  /** Throws instead of toasting, for a caller that shows its own error. */
  rethrow?: boolean;
  /** Waits for earlier writes with any of these keys, so removing a row can't beat adding it. */
  queueKey?: string | readonly string[];
  /** The queries to refetch once the write settles; every board query when left out. */
  invalidates?: readonly BoardQueryName[];
};

/** Resolves to null if the server refuses the write, unless it rethrows. */
type Write = {
  <T>(write: BoardWrite<T> & { rethrow: true }): Promise<T>;
  <T>(write: BoardWrite<T>): Promise<T | null>;
};

/** A plain patch, or one computed from each cached copy with the inverse that undoes it. */
type TaskChange =
  | [patch: TaskPatch]
  | [
      patchFor: (task: AmpTaskRowFragment) => TaskPatch,
      inverse: (task: AmpTaskRowFragment) => TaskPatch,
    ];

type BoardContextValue = {
  currentUserId: string | undefined;
  members: BoardMember[];
  memberById: Map<string, BoardMember>;
  isLoadingMembers: boolean;
  /** A workflow's statuses, as a task in one of its projects can take them. */
  statusesFor: (
    workflowId: string | null | undefined,
  ) => AmpWorkflowStatusFragment[];
  tags: Tag[];
  tasks: TaskActions;
  projects: ProjectActions;
  /** Runs a write the cache shows before the server answers, for writes the actions above don't cover. */
  write: Write;
  /** Opens the new-task dialog, with whatever the context already implies. */
  openCreateTask: (preset?: CreatePreset) => void;
  /** Opens the new-task dialog starting in this status, as a status group's button does. */
  openCreateTaskInStatus: (
    status: AmpWorkflowStatusFragment,
    preset: CreatePreset,
  ) => void;
};

type CreateDialog = { preset: CreatePreset; start: TaskStart };

export type CreateTaskDialogProps = CreateDialog & { onClose: () => void };

type ProviderProps = {
  children: ReactNode;
  /** Passed in rather than imported, since the dialog itself reads the board. */
  createTaskDialog: ComponentType<CreateTaskDialogProps>;
};

const BoardContext = createContext<BoardContextValue | undefined>(undefined);

/** Refetches these board queries, every one when null, then lets go of the made-here tasks they now hold. */
async function refetchBoard(
  queryClient: QueryClient,
  names: readonly BoardQueryName[] | null,
) {
  await queryClient.invalidateQueries({ predicate: boardQueryMatcher(names) });
  dropLoadedCreatedTasks(queryClient);
}

/** How often the board checks for changes made elsewhere, such as by the agent. */
const CHANGE_POLL_MS = 10_000;

const selectLatestEventAt = (data: LatestActivityQuery) =>
  data.latestActivityAt ?? null;

// Outlives the provider, so changes made while the user was elsewhere in the app refetch on return.
let seenLatestEventAt: string | null | undefined;

/**
 * The area's shared lookups and writes. Writes land in the cache at once and
 * roll back if the server refuses them, so every view reacts instantly.
 */
export const ProjectsBoardProvider = ({
  children,
  createTaskDialog: CreateTaskDialog,
}: ProviderProps) => {
  const queryClient = useQueryClient();
  const showError = useBoardErrorToast();
  const { user } = useUser();
  const currentUserId = user ? String(user.id) : undefined;
  const {
    members,
    byId: memberById,
    isLoading: isLoadingMembers,
  } = useWorkspaceMembers();
  const { statusesByWorkflow } = useWorkflows();
  useSyncTaskApprovals((client) => refetchBoard(client, null));
  const { groups } = useGroups();
  const { tags } = useTags();
  const [createDialog, setCreateDialog] = useState<CreateDialog | null>(null);

  // On a project's pages, a new task belongs to that project. Only those routes name a projectId, and a parent route sees its children's params.
  const { projectId: projectInView } = useParams<{ projectId?: string }>();
  useHotkeys(
    CREATE_TASK_SHORTCUT,
    () =>
      setCreateDialog({
        preset: { projectId: projectInView },
        start: { kind: "project-default" },
      }),
    {
      enabled: createDialog === null,
      preventDefault: true,
      ignoreEventWhen: shouldIgnoreCreateTaskShortcut,
    },
  );

  // Actions read lookups through a ref, so they keep one identity and memoized rows don't re-render.
  const latestLookups = {
    memberById,
    statusesByWorkflow,
    tags,
    groups,
    currentUserId,
  };
  const lookups = useRef(latestLookups);
  lookups.current = latestLookups;

  const pendingWrites = useRef(0);
  // What settled writes changed, refetched together once none is in flight; null stands for every board query.
  const staleQueries = useRef<BoardQueryName[] | null>([]);
  // The last request queued for each key, so writes to one task go out in order.
  const queues = useRef(new Map<string, Promise<unknown>>());

  const { refetchInterval } = useGovernedRefetchInterval<string | null>({
    baseMs: CHANGE_POLL_MS,
  });
  const { data: latestEventAt } = useLatestActivityQuery(undefined, {
    select: selectLatestEventAt,
    staleTime: 0,
    refetchInterval,
  });
  useEffect(() => {
    if (latestEventAt === undefined) return;
    if (
      seenLatestEventAt !== undefined &&
      seenLatestEventAt !== latestEventAt
    ) {
      // Refetching mid-write would briefly undo the write, so it waits for the writes to settle.
      if (pendingWrites.current > 0) staleQueries.current = null;
      else void refetchBoard(queryClient, null);
    }
    seenLatestEventAt = latestEventAt;
  }, [latestEventAt, queryClient]);

  const write = useCallback(
    async <T,>({
      apply,
      request,
      errorTitle,
      rethrow = false,
      queueKey,
      invalidates,
    }: BoardWrite<T>): Promise<T | null> => {
      pendingWrites.current += 1;
      const matches = boardQueryMatcher(invalidates ?? null);
      // A fetch in flight would land data from before the write. Cancelled, it runs again after the write settles; a first load isn't patched, so it is left to finish.
      await queryClient.cancelQueries({
        predicate: (query) => query.state.data !== undefined && matches(query),
      });
      const revert = apply();
      const keys = queueKey === undefined ? [] : [queueKey].flat();
      const earlier = keys.flatMap((key) => queues.current.get(key) ?? []);
      const queued =
        earlier.length > 0
          ? Promise.allSettled(earlier).then(request)
          : request();
      for (const key of keys) queues.current.set(key, queued);
      void Promise.allSettled([queued]).then(() => {
        for (const key of keys) {
          if (queues.current.get(key) === queued) queues.current.delete(key);
        }
      });
      try {
        return await queued;
      } catch (error) {
        // Undoes only this write, so others still in flight keep their changes; the refetch below settles the rest.
        revert?.();
        if (rethrow) throw error;
        showError(errorTitle, error);
        return null;
      } finally {
        pendingWrites.current -= 1;
        staleQueries.current =
          invalidates && staleQueries.current
            ? [...staleQueries.current, ...invalidates]
            : null;
        // Refetching while another write is in flight would briefly undo it.
        if (pendingWrites.current === 0) {
          const stale = staleQueries.current;
          staleQueries.current = [];
          void refetchBoard(queryClient, stale);
        }
      }
    },
    [queryClient, showError],
  ) as Write;

  const applyTaskPatch = useCallback(
    (taskId: string, update: TaskUpdate | null) => {
      updateCached<AmpBoardTasksQuery>(queryClient, "AmpBoardTasks", (data) =>
        patchTaskList(data, taskId, update),
      );
      // A dropped task's own page is left to its refetch; a dropped subtask leaves its parent's.
      updateCached<AmpBoardTaskQuery>(queryClient, "AmpBoardTask", (data) =>
        patchTaskDetail(data, taskId, update),
      );
    },
    [queryClient],
  );

  /**
   * Patches every cached copy of a task. Its undo leaves later writes alone: `inverse`
   * runs on each copy as it is then, and a plain patch's fields revert only if unchanged.
   */
  const patchTask = useCallback(
    (taskId: string, ...[change, inverse]: TaskChange) => {
      const current = findTask(queryClient, taskId);
      const updatedAt = new Date().toISOString();
      applyTaskPatch(
        taskId,
        typeof change === "function"
          ? (task) => ({ ...change(task), updated_at: updatedAt })
          : { ...change, updated_at: updatedAt },
      );
      if (!current) return undefined;
      if (inverse) return () => applyTaskPatch(taskId, inverse);
      if (typeof change === "function") return undefined;
      const previous = previousValues(current, change);
      return () =>
        applyTaskPatch(taskId, (task) =>
          unchangedSince(task, change, previous),
        );
    },
    [queryClient, applyTaskPatch],
  );

  const tasks = useMemo<TaskActions>(() => {
    // The queue keeps one task's writes in order, so a quick removal can't beat its add.
    const writeTask = <T,>(taskId: string, task: BoardWrite<T>) =>
      write({ queueKey: `task:${taskId}`, invalidates: TASK_QUERIES, ...task });
    const updateRequest = (taskId: string, set: AmpTasksSetInput) => async () =>
      expectRow(
        await useUpdateAmpBoardTaskMutation.fetcher({ id: taskId, set })(),
        (result) => result.update_amp_tasks_by_pk,
      );
    const update = (
      taskId: string,
      set: AmpTasksSetInput & TaskPatch,
      errorTitle: string,
    ) =>
      void writeTask(taskId, {
        errorTitle,
        apply: () => patchTask(taskId, set),
        request: updateRequest(taskId, set),
      });

    /** Sets a task's owner or assignee rows of one kind, users or groups, leaving the other kind in place. */
    const replaceTaskPeople = (
      taskId: string,
      type: TaskPersonType,
      column: "user_id" | "user_group_id",
      ids: string[],
    ) => {
      const task = findTask(queryClient, taskId);
      if (!task || isPendingId(taskId)) return;
      const field = TASK_PERSON_FIELD[type];
      const wanted = new Set(ids);
      const rows = task[field].filter((person) => person[column] != null);
      const removed = rows.filter(
        (person) => !wanted.has(String(person[column])),
      );
      const had = new Set(rows.map((person) => String(person[column])));
      const added = ids.flatMap((id) => {
        if (had.has(id)) return [];
        if (column === "user_id") {
          const member = lookups.current.memberById.get(id);
          return member ? [pendingTaskUser(member, type)] : [];
        }
        const group = lookups.current.groups.find(
          (candidate) => candidate.id === id,
        );
        return group ? [pendingTaskGroup(group, type)] : [];
      });
      if (removed.length === 0 && added.length === 0) return;
      const addedIds = new Set(added.map((person) => person.id));
      const removedIds = new Set(removed.map((person) => person.id));
      void writeTask(taskId, {
        errorTitle:
          type === "owner"
            ? "Couldn't change the owner"
            : "Couldn't change the assignees",
        apply: () =>
          patchTask(
            taskId,
            (current) => ({
              [field]: [
                ...current[field].filter(
                  (person) => !removedIds.has(person.id),
                ),
                ...added,
              ],
            }),
            (current) => ({
              [field]: [
                ...current[field].filter((person) => !addedIds.has(person.id)),
                ...removed,
              ],
            }),
          ),
        request: () =>
          useUpdateAmpBoardTaskUsersMutation.fetcher({
            remove: {
              task_id: { _eq: taskId },
              type: { _eq: type },
              [column]: { _in: removed.map((person) => person[column]) },
            },
            objects: added.map((person) => ({
              task_id: taskId,
              type,
              [column]: person[column],
            })),
          })(),
      });
    };

    return {
      create: async (input) => {
        const { memberById, statusesByWorkflow, tags } = lookups.current;
        const project = findProject(queryClient, input.projectId);
        // A project with no workflow takes no tasks, so it has no statuses to start one in.
        const start = newTaskStartOf({
          parent: input.parent,
          defaultWorkflowId: project?.workflow_id ?? null,
          statusesFor: (workflowId) =>
            (workflowId && statusesByWorkflow.get(workflowId)) || [],
        });
        const parentTaskId = start.input.parentTaskId ?? null;
        const author = lookups.current.currentUserId
          ? memberById.get(lookups.current.currentUserId)
          : undefined;
        // The server gives an ownerless task to its creator, so the draft does too.
        const ownerIds =
          input.ownerUserIds ?? (author ? [String(author.id)] : []);
        const draftTags = (input.tags ?? []).map((name) =>
          tagOrPending(tags, name),
        );
        const now = new Date().toISOString();
        const pendingId = newPendingId();
        const optimistic: AmpTaskRowFragment | null =
          project && start.columns
            ? {
                id: pendingId,
                project_id: project.id,
                ...start.columns,
                title: input.title,
                description: input.description ?? null,
                due_at: input.dueAt ?? null,
                // New tasks go to the end of the project, as the server places them.
                position: nextTaskPosition(queryClient, {
                  projectId: project.id,
                  parentTaskId,
                }),
                deleted_at: null,
                created_at: now,
                updated_at: now,
                created_by: author?.id ?? null,
                created_by_user: author ?? null,
                project: {
                  id: project.id,
                  title: project.title,
                  icon: project.icon,
                  workflow_id: project.workflow_id,
                },
                owners: ownerIds.flatMap((id) => {
                  const owner = memberById.get(String(id));
                  return owner ? [pendingTaskUser(owner, "owner")] : [];
                }),
                assignees: [
                  ...(input.assigneeUserIds ?? []).flatMap((id) => {
                    const assignee = memberById.get(id);
                    return assignee
                      ? [pendingTaskUser(assignee, "assignee")]
                      : [];
                  }),
                  ...(input.assigneeGroupIds ?? []).flatMap((id) => {
                    const group = lookups.current.groups.find(
                      (candidate) => candidate.id === id,
                    );
                    return group ? [pendingTaskGroup(group, "assignee")] : [];
                  }),
                ],
                tags: draftTags.map((tag) => ({ id: newPendingId(), tag })),
                reviewers: [],
                subtasks_total: { aggregate: { count: 0 } },
                subtasks_done: { aggregate: { count: 0 } },
                first_subtask: [],
                attachments: [],
                blockers: [],
              }
            : null;
        const patchSubtasks = (
          parentId: string,
          change: (subtasks: AmpTaskRowFragment[]) => AmpTaskRowFragment[],
        ) =>
          updateCached<AmpBoardTaskQuery>(
            queryClient,
            useAmpBoardTaskQuery.getKey({ id: parentId }),
            (data) =>
              data.amp_tasks_by_pk
                ? {
                    ...data,
                    amp_tasks_by_pk: {
                      ...data.amp_tasks_by_pk,
                      subtasks: change(data.amp_tasks_by_pk.subtasks),
                    },
                  }
                : data,
          );
        const result = await write({
          errorTitle: `Couldn't create the ${
            parentTaskId ? TERMS.subtask.singular : TERMS.task.singular
          }`,
          rethrow: true,
          queueKey: parentTaskId ? `task:${parentTaskId}` : undefined,
          // A tag named for the first time is created with the task.
          invalidates: draftTags.some((tag) => isPendingId(tag.id))
            ? [...TASK_QUERIES, "AmpBoardTags"]
            : TASK_QUERIES,
          apply: () => {
            if (!optimistic || !project) return undefined;
            if (parentTaskId) {
              patchSubtasks(parentTaskId, (subtasks) => [
                ...subtasks,
                optimistic,
              ]);
              const undoCount = patchTask(
                parentTaskId,
                (parent) => withSubtaskCounts(parent, { total: 1 }),
                (parent) => withSubtaskCounts(parent, { total: -1 }),
              );
              return () => {
                applyTaskPatch(pendingId, null);
                undoCount?.();
              };
            }
            queryClient.setQueryData<AmpBoardTasksQuery>(
              CREATED_TASKS_KEY,
              (data) => ({
                amp_tasks: [...(data?.amp_tasks ?? []), optimistic],
              }),
            );
            return () => applyTaskPatch(pendingId, null);
          },
          request: async () => {
            const created = await useCreateAmpBoardTaskMutation.fetcher({
              projectId: input.projectId,
              input: {
                title: input.title,
                description: input.description,
                ...start.input,
                ownerUserIds: input.ownerUserIds,
                assignees: [
                  ...(input.assigneeUserIds ?? []).map((userId) => ({
                    userId,
                  })),
                  ...(input.assigneeGroupIds ?? []).map((userGroupId) => ({
                    userGroupId,
                  })),
                ],
                dueAt: input.dueAt,
                tags: input.tags,
              },
            })();
            // The real id makes the row writable before the refetch lands.
            const { id } = created.createAmpTask;
            if (parentTaskId) {
              patchSubtasks(parentTaskId, (subtasks) =>
                swapPendingId(subtasks, pendingId, id),
              );
            } else {
              updateCached<AmpBoardTasksQuery>(
                queryClient,
                "AmpBoardTasks",
                (data) => ({
                  ...data,
                  amp_tasks: swapPendingId(data.amp_tasks, pendingId, id),
                }),
              );
            }
            return created;
          },
        });
        return result.createAmpTask.id;
      },

      setStatus: (task, status) => {
        if (
          (status.id === task.status_id && task.deleted_at === null) ||
          isPendingId(task.id)
        ) {
          return;
        }
        void writeTask(task.id, {
          errorTitle: "Couldn't change the status",
          apply: () => {
            const undoPatch = patchTask(task.id, {
              status_id: status.id,
              status,
              deleted_at: null,
            });
            // The new status's group may have no page loaded, as when it was empty, and the old group's refetch drops the task before that page exists.
            const moved = findTask(queryClient, task.id);
            const created =
              queryClient.getQueryData<AmpBoardTasksQuery>(CREATED_TASKS_KEY);
            if (
              !moved ||
              created?.amp_tasks.some((row) => row.id === task.id)
            ) {
              return undoPatch;
            }
            queryClient.setQueryData<AmpBoardTasksQuery>(CREATED_TASKS_KEY, {
              amp_tasks: [...(created?.amp_tasks ?? []), moved],
            });
            return () => {
              undoPatch?.();
              updateCached<AmpBoardTasksQuery>(
                queryClient,
                CREATED_TASKS_KEY,
                (data) => ({
                  amp_tasks: data.amp_tasks.filter((row) => row.id !== task.id),
                }),
              );
            };
          },
          request: () =>
            useSetAmpBoardTaskStatusMutation.fetcher({
              id: task.id,
              statusId: status.id,
            })(),
        });
      },

      setArchived: (taskId, isArchived) => {
        const task = findTask(queryClient, taskId);
        if (!task || isPendingId(taskId)) return;
        if ((task.deleted_at !== null) === isArchived) return;
        const archivedAt = isArchived ? new Date().toISOString() : null;
        update(
          taskId,
          { deleted_at: archivedAt },
          isArchived
            ? `Couldn't archive the ${TERMS.task.singular}`
            : `Couldn't restore the ${TERMS.task.singular}`,
        );
      },

      setSubtaskStatus: (taskId, category) => {
        const subtask = findTask(queryClient, taskId);
        if (!subtask?.parent_task_id || isPendingId(taskId)) return;
        if (subtask.status_category === category) return;
        const parentTaskId = subtask.parent_task_id;
        const done =
          (category === "done" ? 1 : 0) -
          (subtask.status_category === "done" ? 1 : 0);
        void writeTask(taskId, {
          errorTitle: "Couldn't change the status",
          request: updateRequest(taskId, { status_category: category }),
          apply: () => {
            const undoStatus = patchTask(taskId, { status_category: category });
            const undoCount =
              done === 0
                ? undefined
                : patchTask(
                    parentTaskId,
                    (parent) => withSubtaskCounts(parent, { done }),
                    (parent) => withSubtaskCounts(parent, { done: -done }),
                  );
            return () => {
              undoStatus?.();
              undoCount?.();
            };
          },
        });
      },

      setPeople: (taskId, type, userIds) =>
        replaceTaskPeople(taskId, type, "user_id", userIds),

      setAssigneeGroups: (taskId, userGroupIds) =>
        replaceTaskPeople(taskId, "assignee", "user_group_id", userGroupIds),

      setDueDate: (taskId, dueAt) =>
        update(taskId, { due_at: dueAt }, "Couldn't change the due date"),

      setText: (taskId, fields) =>
        update(taskId, fields, `Couldn't save the ${TERMS.task.singular}`),

      addTag: (taskId, name) => {
        const tag = tagOrPending(lookups.current.tags, name);
        const taskTagId = newPendingId();
        if (
          findTask(queryClient, taskId)?.tags.some(
            (taskTag) =>
              taskTag.tag?.name.toLowerCase() === tag.name.toLowerCase(),
          )
        ) {
          return;
        }
        void writeTask(taskId, {
          errorTitle: "Couldn't add the tag",
          // A tag named for the first time is created with it.
          invalidates: isPendingId(tag.id)
            ? [...TASK_QUERIES, "AmpBoardTags"]
            : TASK_QUERIES,
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                tags: [...task.tags, { id: taskTagId, tag }],
              }),
              (task) => ({
                tags: task.tags.filter((taskTag) => taskTag.id !== taskTagId),
              }),
            ),
          request: async () => {
            const result = await useTagAmpBoardTaskMutation.fetcher({
              taskId,
              name,
            })();
            // A new tag's real id lets it be removed before the refetch lands.
            const { tagId } = result.tagAmpTask;
            if (isPendingId(tag.id)) {
              applyTaskPatch(taskId, (task) => ({
                tags: task.tags.map((taskTag) =>
                  taskTag.id === taskTagId
                    ? { ...taskTag, tag: { ...tag, id: tagId } }
                    : taskTag,
                ),
              }));
            }
            return result;
          },
        });
      },

      removeTag: (taskId, tagId) => {
        const removed = findTask(queryClient, taskId)?.tags.find(
          (candidate) => candidate.tag?.id === tagId,
        );
        // A brand-new tag has no id to remove by until the server makes it.
        if (!removed || isPendingId(tagId)) return;
        void writeTask(taskId, {
          errorTitle: "Couldn't remove the tag",
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                tags: task.tags.filter((taskTag) => taskTag.tag?.id !== tagId),
              }),
              (task) => ({ tags: [...task.tags, removed] }),
            ),
          request: () =>
            useUntagAmpBoardTaskMutation.fetcher({ taskId, tagId })(),
        });
      },

      addBlocker: (taskId, blocker) => {
        if (
          findTask(queryClient, taskId)?.blockers.some(
            (relation) => relation.source_task?.id === blocker.id,
          )
        ) {
          return;
        }
        const relationId = newPendingId();
        void writeTask(taskId, {
          errorTitle: "Couldn't add the blocker",
          invalidates: BLOCKER_QUERIES,
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                blockers: [
                  ...task.blockers,
                  { id: relationId, source_task: blocker },
                ],
              }),
              (task) => ({
                blockers: task.blockers.filter(
                  (relation) => relation.id !== relationId,
                ),
              }),
            ),
          request: () =>
            useAddAmpBoardTaskBlockerMutation.fetcher({
              taskId,
              blockerId: blocker.id,
            })(),
        });
      },

      removeBlocker: (taskId, blockerId) => {
        const removed = findTask(queryClient, taskId)?.blockers.find(
          (relation) => relation.source_task?.id === blockerId,
        );
        if (!removed) return;
        void writeTask(taskId, {
          errorTitle: "Couldn't remove the blocker",
          invalidates: BLOCKER_QUERIES,
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                blockers: task.blockers.filter(
                  (relation) => relation.source_task?.id !== blockerId,
                ),
              }),
              (task) => ({ blockers: [...task.blockers, removed] }),
            ),
          request: () =>
            useRemoveAmpBoardTaskBlockerMutation.fetcher({
              taskId,
              blockerId,
            })(),
        });
      },

      reorder: (positions) => {
        // A task still being created has no row to move yet.
        const saved = positions.filter(({ id }) => !isPendingId(id));
        if (saved.length === 0) return;
        void write({
          errorTitle: `Couldn't reorder the ${TERMS.task.plural}`,
          queueKey: saved.map(({ id }) => `task:${id}`),
          invalidates: TASK_QUERIES,
          apply: () => {
            const reverts = saved.map(({ id, position }) =>
              patchTask(id, { position }),
            );
            return () => {
              for (const revert of reverts) revert?.();
            };
          },
          request: () =>
            useReorderAmpBoardTasksMutation.fetcher({
              updates: saved.map(({ id, position }) => ({
                where: { id: { _eq: id } },
                _set: { position },
              })),
            })(),
        });
      },

      move: (task, move) => {
        if (isPendingId(task.id)) return;
        const isShifted = (row: AmpTaskRowFragment) =>
          row.project_id === task.project_id &&
          row.parent_task_id === null &&
          row.id !== task.id &&
          row.position >= move.from &&
          row.position <= move.until;
        void write({
          errorTitle: `Couldn't reorder the ${TERMS.task.plural}`,
          // Each move shifts a range read from the last, so a project's moves go out in order.
          queueKey: `project-order:${task.project_id}`,
          invalidates: TASK_QUERIES,
          apply: () =>
            revertableUpdate(queryClient, () =>
              updateCached<AmpBoardTasksQuery>(
                queryClient,
                "AmpBoardTasks",
                (data) => ({
                  ...data,
                  amp_tasks: data.amp_tasks.map((row) =>
                    row.id === task.id
                      ? { ...row, position: move.to }
                      : isShifted(row)
                        ? { ...row, position: row.position + move.by }
                        : row,
                  ),
                }),
              ),
            ),
          request: async () =>
            expectRow(
              await useMoveAmpBoardTaskMutation.fetcher({
                id: task.id,
                projectId: task.project_id,
                ...move,
              })(),
              (result) => result.update_amp_tasks_by_pk,
            ),
        });
      },

      addReviewer: (taskId, { userId, userGroupId }) => {
        const user = userId
          ? lookups.current.memberById.get(userId)
          : undefined;
        const group = userGroupId
          ? lookups.current.groups.find(
              (candidate) => candidate.id === userGroupId,
            )
          : undefined;
        // Adding someone already reviewing would be a duplicate the server refuses.
        if (
          findTask(queryClient, taskId)?.reviewers.some(
            (reviewer) =>
              (userId !== null && String(reviewer.user_id) === userId) ||
              (userGroupId !== null &&
                String(reviewer.user_group_id) === userGroupId),
          )
        ) {
          return;
        }
        const pendingId = newPendingId();
        void writeTask(taskId, {
          errorTitle: "Couldn't add the reviewer",
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                reviewers: [
                  ...task.reviewers,
                  {
                    id: pendingId,
                    type: "reviewer",
                    user_id: userId,
                    user_group_id: userGroupId,
                    required: false,
                    member_rule: null,
                    user: user ?? null,
                    user_group: group
                      ? { id: group.id, name: group.name }
                      : null,
                  },
                ],
              }),
              (task) => ({
                reviewers: task.reviewers.filter(
                  (reviewer) => reviewer.id !== pendingId,
                ),
              }),
            ),
          request: async () => {
            const result = await useUpdateAmpBoardTaskUsersMutation.fetcher({
              remove: { id: { _in: [] } },
              objects: [
                {
                  task_id: taskId,
                  type: "reviewer",
                  user_id: userId,
                  user_group_id: userGroupId,
                },
              ],
            })();
            const id = result.insert_amp_task_users?.returning[0]?.id;
            if (id) {
              applyTaskPatch(taskId, (task) => ({
                reviewers: swapPendingId(task.reviewers, pendingId, id),
              }));
            }
            return result;
          },
        });
      },

      removeReviewer: (taskId, removed) => {
        void writeTask(taskId, {
          errorTitle: "Couldn't remove the reviewer",
          apply: () =>
            patchTask(
              taskId,
              (task) => ({
                reviewers: task.reviewers.filter(
                  (reviewer) => reviewer.id !== removed.id,
                ),
              }),
              (task) => ({ reviewers: [...task.reviewers, removed] }),
            ),
          request: () =>
            useUpdateAmpBoardTaskUsersMutation.fetcher({
              // A reviewer still being added is found by who it is rather than by its id.
              remove: {
                task_id: { _eq: taskId },
                type: { _eq: "reviewer" },
                ...(removed.user_group_id
                  ? { user_group_id: { _eq: removed.user_group_id } }
                  : { user_id: { _eq: removed.user_id } }),
              },
              objects: [],
            })(),
        });
      },

      removeSubtask: async (taskId) => {
        await writeTask(taskId, {
          errorTitle: `Couldn't remove the ${TERMS.subtask.singular}`,
          rethrow: true,
          // A refused removal is put back by the refetch that follows it.
          apply: () => {
            const task = findTask(queryClient, taskId);
            applyTaskPatch(taskId, null);
            if (task?.parent_task_id) {
              const done = task.status_category === "done" ? 1 : 0;
              patchTask(
                task.parent_task_id,
                (parent) =>
                  withSubtaskCounts(parent, { total: -1, done: -done }),
                (parent) => withSubtaskCounts(parent, { total: 1, done }),
              );
            }
            return undefined;
          },
          request: updateRequest(taskId, {
            deleted_at: new Date().toISOString(),
          }),
        });
      },
    };
  }, [queryClient, write, patchTask, applyTaskPatch]);

  const projects = useMemo<ProjectActions>(() => {
    const applyProjectPatch = (projectId: string, patch: ProjectPatch) => {
      updateCached<AmpBoardProjectsQuery>(
        queryClient,
        "AmpBoardProjects",
        (data) => patchProjectList(data, projectId, patch),
      );
      updateCached<AmpBoardProjectQuery>(
        queryClient,
        "AmpBoardProject",
        (data) => patchProjectDetail(data, projectId, patch),
      );
      updateCached<AmpBoardTasksQuery>(queryClient, "AmpBoardTasks", (data) =>
        patchTasksOfProject(data, projectId, patch),
      );
    };

    return {
      update: (projectId, set) => {
        const patch = projectPatch(set);
        // Tasks carry their project's title and icon, and leave the tasks page with it when it's archived.
        const changesTasks =
          set.title != null ||
          set.icon !== undefined ||
          set.deleted_at !== undefined;
        void write({
          errorTitle: `Couldn't save the ${TERMS.project.singular}`,
          queueKey: `project:${projectId}`,
          invalidates: changesTasks
            ? [
                ...PROJECT_QUERIES,
                "AmpBoardTasks",
                "AmpBoardTaskGroups",
                "AmpBoardTask",
              ]
            : PROJECT_QUERIES,
          apply: () => {
            const current = findProject(queryClient, projectId);
            applyProjectPatch(projectId, patch);
            // Archiving or restoring moves the project between the open and archived lists.
            if (set.deleted_at !== undefined && current) {
              moveProjectBetweenLists(queryClient, {
                ...current,
                ...patch,
              });
            }
            if (!current) return undefined;
            const previous = previousValues(current, patch);
            return () => {
              const latest = findProject(queryClient, projectId);
              if (!latest) return;
              const undo = unchangedSince(latest, patch, previous);
              applyProjectPatch(projectId, undo);
              if (undo.deleted_at !== undefined) {
                moveProjectBetweenLists(queryClient, { ...latest, ...undo });
              }
            };
          },
          request: async () =>
            expectRow(
              await useUpdateAmpBoardProjectMutation.fetcher({
                id: projectId,
                set,
              })(),
              (result) => result.update_amp_projects_by_pk,
            ),
        });
      },
      setPeople: (projectId, { ownerIds, memberIds }) => {
        const project = findProject(queryClient, projectId);
        if (!project) return;
        const { memberById } = lookups.current;
        const current = [...project.owners, ...project.members];
        const typeOf = new Map<string, "owner" | "member">();
        for (const person of current) {
          typeOf.set(String(person.user_id), person.type as "owner" | "member");
        }
        // An owner is on the project already, so they aren't a member too.
        for (const id of memberIds ?? []) typeOf.set(id, "member");
        for (const id of ownerIds ?? []) typeOf.set(id, "owner");
        const listed = new Set([...(ownerIds ?? []), ...(memberIds ?? [])]);
        for (const person of current) {
          const id = String(person.user_id);
          const wasListed =
            (person.type === "owner" && ownerIds !== undefined) ||
            (person.type === "member" && memberIds !== undefined);
          if (wasListed && !listed.has(id)) typeOf.delete(id);
        }
        // A change of type replaces the row, since a project has one per person.
        const removed = current.filter(
          (person) => typeOf.get(String(person.user_id)) !== person.type,
        );
        const kept = current.filter((person) => !removed.includes(person));
        const keptIds = new Set(kept.map((person) => String(person.user_id)));
        const added = [...typeOf].flatMap(([id, type]) => {
          const member = memberById.get(id);
          return !keptIds.has(id) && member
            ? [pendingProjectPerson(member, type)]
            : [];
        });
        if (removed.length === 0 && added.length === 0) return;
        const next = [...kept, ...added];
        const patch = {
          owners: next.filter((person) => person.type === "owner"),
          members: next.filter((person) => person.type === "member"),
        };
        const previous = { owners: project.owners, members: project.members };
        void write({
          errorTitle: `Couldn't change who's on the ${TERMS.project.singular}`,
          queueKey: `project:${projectId}`,
          invalidates: PROJECT_QUERIES,
          apply: () => {
            applyProjectPatch(projectId, patch);
            return () => {
              const latest = findProject(queryClient, projectId);
              if (latest) {
                applyProjectPatch(
                  projectId,
                  unchangedSince(latest, patch, previous),
                );
              }
            };
          },
          request: () =>
            useUpdateAmpBoardProjectUsersMutation.fetcher({
              remove: {
                project_id: { _eq: projectId },
                user_id: { _in: removed.map((person) => person.user_id) },
              },
              objects: added.map((person) => ({
                project_id: projectId,
                user_id: person.user_id,
                type: person.type,
              })),
            })(),
        });
      },
      link: ({ projectId, taskId, url, label }) => {
        const linkId = newPendingId();
        const target = { projectId, taskId };
        const { currentUserId: authorId, memberById } = lookups.current;
        const author = authorId ? memberById.get(authorId) : undefined;
        const pending: AmpProjectLinkFragment = {
          id: linkId,
          created_at: new Date().toISOString(),
          external: { label, href: url },
          created_by_user: author ?? null,
          audience: null,
          ad_studio_project_brief_version: null,
          marketing_campaign: null,
          campaign_artifact: null,
          amp_file: null,
          amp_opportunity: null,
          amp_action: null,
          amp_action_plan: null,
          llm_chat_session: null,
          sync: null,
        };
        void write({
          errorTitle: "Couldn't add the link",
          invalidates: LINK_QUERIES,
          apply: () => {
            // Newest first, as both pages order their attachments.
            patchLinks(queryClient, target, (links) => [pending, ...links]);
            return () =>
              patchLinks(queryClient, target, (links) =>
                links.filter((link) => link.id !== linkId),
              );
          },
          request: async () => {
            const result = await useLinkAmpBoardWorkMutation.fetcher({
              projectId,
              taskId: taskId ?? null,
              target: {
                kind: "external",
                external: {
                  system: "web",
                  kind: "link",
                  id: url,
                  label,
                  href: url,
                },
              },
            })();
            // The real id lets the link be removed before the refetch lands.
            patchLinks(queryClient, target, (links) =>
              swapPendingId(links, linkId, result.linkAmpProjectWork.linkId),
            );
            return result;
          },
        });
      },
      attach: async ({ projectId, taskId, target }) => {
        await write({
          errorTitle: "Couldn't add the attachment",
          invalidates: LINK_QUERIES,
          // The row shows once the refetch brings it, since only the server knows what it describes.
          apply: () => undefined,
          request: () =>
            useLinkAmpBoardWorkMutation.fetcher({
              projectId,
              taskId: taskId ?? null,
              target,
            })(),
          // The picker stays open and shows the error itself.
          rethrow: true,
        });
      },
      unlink: (linkId) => {
        // A link still being added has no row to remove yet.
        if (isPendingId(linkId)) return;
        void write({
          errorTitle: "Couldn't remove the attachment",
          invalidates: LINK_QUERIES,
          // A refused removal is put back by the refetch that follows it.
          apply: () => {
            patchLinks(queryClient, null, (links) =>
              links.filter((link) => link.id !== linkId),
            );
            updateCached<AmpBoardTaskQuery>(
              queryClient,
              "AmpBoardTask",
              (data) => ({
                ...data,
                subtask_links: data.subtask_links.filter(
                  (link) => link.id !== linkId,
                ),
              }),
            );
            // A project's page shows its tasks' attachments too.
            updateCached<AmpBoardProjectQuery>(
              queryClient,
              "AmpBoardProject",
              (data) => ({
                ...data,
                task_links: data.task_links.filter(
                  (link) => link.id !== linkId,
                ),
              }),
            );
            return undefined;
          },
          request: async () => {
            const result = expectRow(
              await useUnlinkAmpBoardWorkMutation.fetcher({ id: linkId })(),
              (result) => result.delete_amp_project_links_by_pk,
            );
            // Project chips read links outside the board's queries.
            void invalidateLinkedProjects(queryClient);
            return result;
          },
        });
      },
    };
  }, [queryClient, write]);

  const statusesFor = useCallback(
    (workflowId: string | null | undefined) =>
      (workflowId && statusesByWorkflow.get(workflowId)) || EMPTY_STATUSES,
    [statusesByWorkflow],
  );

  const value = useMemo<BoardContextValue>(
    () => ({
      currentUserId,
      members,
      memberById,
      isLoadingMembers,
      statusesFor,
      tags,
      tasks,
      projects,
      write,
      openCreateTask: (preset = {}) =>
        setCreateDialog({ preset, start: { kind: "project-default" } }),
      openCreateTaskInStatus: (status, preset) =>
        setCreateDialog({ preset, start: { kind: "status", status } }),
    }),
    [
      currentUserId,
      members,
      memberById,
      isLoadingMembers,
      statusesFor,
      tags,
      tasks,
      projects,
      write,
    ],
  );

  return (
    <BoardContext.Provider value={value}>
      {children}
      {createDialog && (
        <Suspense fallback={null}>
          <CreateTaskDialog
            preset={createDialog.preset}
            start={createDialog.start}
            onClose={() => setCreateDialog(null)}
          />
        </Suspense>
      )}
    </BoardContext.Provider>
  );
};

export const useBoard = (): BoardContextValue => {
  const value = useContext(BoardContext);
  if (!value) {
    throw new Error("useBoard must be used within <ProjectsBoardProvider>");
  }
  return value;
};

const EMPTY_STATUSES: AmpWorkflowStatusFragment[] = [];

/** Hasura answers a write it isn't allowed to make with null rather than an error, so a null row is a refusal. */
function expectRow<T>(result: T, row: (result: T) => unknown): T {
  if (row(result) == null) {
    throw new Error("It may have been deleted, or you may not have access.");
  }
  return result;
}

/** Puts a just-archived project on the archived list and a restored one back on the open list. */
function moveProjectBetweenLists(
  queryClient: QueryClient,
  project: AmpProjectRowFragment,
) {
  const [from, to] = project.deleted_at
    ? [OPEN_PROJECTS_VARIABLES, ARCHIVED_PROJECTS_VARIABLES]
    : [ARCHIVED_PROJECTS_VARIABLES, OPEN_PROJECTS_VARIABLES];
  updateCached<AmpBoardProjectsQuery>(
    queryClient,
    useAmpBoardProjectsQuery.getKey(from),
    (data) => ({
      ...data,
      amp_projects: data.amp_projects.filter(
        (candidate) => candidate.id !== project.id,
      ),
    }),
  );
  updateCached<AmpBoardProjectsQuery>(
    queryClient,
    useAmpBoardProjectsQuery.getKey(to),
    (data) => ({
      ...data,
      amp_projects: [
        project,
        ...data.amp_projects.filter((candidate) => candidate.id !== project.id),
      ],
    }),
  );
}

/** The cached fields a project write changes. */
function projectPatch(set: AmpProjectsSetInput): ProjectPatch {
  const patch: ProjectPatch = {};
  if (set.title != null) patch.title = set.title;
  if (set.status != null) patch.status = set.status;
  if (set.description !== undefined) patch.description = set.description;
  if (set.start_at !== undefined) patch.start_at = set.start_at;
  if (set.due_at !== undefined) patch.due_at = set.due_at;
  if (set.icon !== undefined) patch.icon = set.icon;
  if (set.deleted_at !== undefined) patch.deleted_at = set.deleted_at;
  if (set.brand_profile_id !== undefined) {
    patch.brand_profile_id = set.brand_profile_id;
  }
  return patch;
}

/** An owner or assignee drawn before the server returns their row. */
function pendingTaskUser(
  user: AmpBoardUserFragment,
  type: TaskPersonType,
): AmpTaskUserFragment {
  return {
    id: newPendingId(),
    type,
    user_id: user.id,
    user_group_id: null,
    required: false,
    member_rule: null,
    user,
    user_group: null,
  };
}

/** An assignee or reviewer group drawn before the server returns its row. */
function pendingTaskGroup(
  group: { id: string; name: string },
  type: AmpTaskUserFragment["type"],
): AmpTaskUserFragment {
  return {
    id: newPendingId(),
    type,
    user_id: null,
    user_group_id: group.id,
    required: false,
    member_rule: null,
    user: null,
    user_group: { id: group.id, name: group.name },
  };
}

function pendingProjectPerson(
  user: AmpBoardUserFragment,
  type: "owner" | "member",
): AmpProjectUserFragment {
  return {
    id: newPendingId(),
    type,
    user_id: user.id,
    user,
  };
}

/** The workspace's tag of that name, or a stand-in until the server creates it. */
function tagOrPending(tags: Tag[], name: string): Tag {
  const lower = name.trim().toLowerCase();
  return (
    tags.find((tag) => tag.name.toLowerCase() === lower) ?? {
      id: newPendingId(),
      name,
      color: null,
    }
  );
}

type Links = AmpProjectLinkFragment[];

/** Runs an update over one project or task page's attachments, or over every page's when the target is null. */
function patchLinks(
  queryClient: QueryClient,
  target: { projectId: string; taskId?: string } | null,
  update: (links: Links) => Links,
) {
  if (!target || target.taskId) {
    updateCached<AmpBoardTaskQuery>(
      queryClient,
      target?.taskId
        ? useAmpBoardTaskQuery.getKey({ id: target.taskId })
        : "AmpBoardTask",
      (data) =>
        data.amp_tasks_by_pk
          ? {
              ...data,
              amp_tasks_by_pk: {
                ...data.amp_tasks_by_pk,
                links: update(data.amp_tasks_by_pk.links),
              },
            }
          : data,
    );
  }
  // A project page lists only the project's own attachments, not its tasks'.
  if (!target?.taskId) {
    updateCached<AmpBoardProjectQuery>(
      queryClient,
      target
        ? useAmpBoardProjectQuery.getKey({ id: target.projectId })
        : "AmpBoardProject",
      (data) =>
        data.amp_projects_by_pk
          ? {
              ...data,
              amp_projects_by_pk: {
                ...data.amp_projects_by_pk,
                links: update(data.amp_projects_by_pk.links),
              },
            }
          : data,
    );
  }
}
